// ══════════════════════════════════════════════════════════════════
//  밤 ↔ 낮 어댑터 — **낮의 1단계이자 유일한 접점.**
//
//  ■ 이 파일은 밤 밖에 서서 World 인스턴스를 읽고, 밤이 받는 opts 를 만들어 준다.
//    그래서 밤이 지금 무슨 상태든(트리 리모델링 중이든 밸런스가 무너져 있든)
//    낮은 독립으로 자란다.
//    *구 헤더는 여기서 **「밤 코드를 한 줄도 안 고친다」**고 적었다. 그것은 낮을
//     혼자 세우는 동안의 규율이었고 그 목적을 다했다 — 낮이 다 서고 나니 밤에
//     받을 자리가 없다는 사실만 남았다(이 파일의 절반이 `TODO(밤)` 이었다).
//     그래서 밤에 문을 하나 팠다: **`gb/dayin.js`**. 밤이 읽는 곳은 거기 한 곳뿐이고,
//     밤의 나머지 코드는 낮이 있는 줄도 모른다 — 규율의 알맹이는 그대로다.*
//
//  ■ **`world.report()` 를 쓰지 않는다.** 그것은 회색박스 화면용 한글 덤프다
//    (키가 한글이고 값이 문자열이다 — 「손실 배율」이 `"×0.5 [78~86]"`). 낮이
//    그걸 파싱하면 **밤의 표시 포맷이 낮의 계약이 된다.** 표시가 바뀌면 낮이
//    깨진다. 그래서 World 인스턴스 필드에서 직접 뽑는다.
//
//  ■ **밤에 아직 없는 훅**은 여기서 `null` 로 만들어 넘긴다. 밤은 모르는 키를
//    무시하므로 **지금 동작이 한 톨도 안 바뀐다.** 밤에 훅이 뚫리면 이 파일만
//    고치면 된다. 각 자리에 `TODO(밤)` 로 무엇이 없는지 적어 둔다.
// ══════════════════════════════════════════════════════════════════

import { resolveDayMods } from './effects.js';
import { nightDogs } from './dogs.js';
import { snapshotBossRecord } from '../gb/boss.js';
import { hydrateGear } from './gacha.js';
import { PART_BY_ID } from '../gb/gear.js';

// ── 밤 → 낮 ───────────────────────────────────────────────────────
// World 인스턴스 하나에서 **낮이 쓸 것만** 뽑는다. 여기 없는 것은 낮이 안 본다.
export function toNightResult(world) {
  if (!world) return null;
  const m = world.m ?? {};
  const p = world.player ?? {};
  const boss = world.bossRecord
    ? { ...world.bossRecord }
    : snapshotBossRecord(world.boss, m);

  return {
    // ── 결과 ────────────────────────────────────────────────
    // ⚠ **`'생존' | '구출' | '다운'` 셋뿐이다.** `'완주'` 는 코드에 없다.
    //   두 갈래(`완주|다운`)로 접으면 **N22 「구출」이 사라진다** — 설계안 6건이
    //   전부 그 실수를 했다. 낮은 셋을 그대로 받는다.
    result: world.result ?? null,          // 미종료면 null
    // N40은 전투 실패 뒤에도 양 찾기 피날레를 끝내므로 최종 result는 '구출'이다.
    // 전투 실패 정산은 별도 표식을 함께 읽어 낮까지 잃지 않는다.
    down: world.result === '다운' || world.finalCombatDown === true,
    rescued: world.result === '구출',

    // ── 밤↔낮 유일한 다리 ───────────────────────────────────
    // 시간적분 평균이다(막판 버티기가 안 통한다 — 명세 5장).
    avgStability: world.avgStability,
    // settle() 이 낸 정산. **낮은 손실 정산표를 다시 읽지 않는다** —
    // 배율표를 두 곳이 들면 언젠가 갈린다. 밤이 낸 것을 그대로 쓴다.
    //   `loss` 는 **소수 마리 수**다(0.13~0.75 등). 낮이 누적해서 정수로 만든다.
    //   다운 밤은 정산표를 안 보고 `DOWN.base + slope × 남은비율` 로 나온다 —
    //   즉 「하한 ×3.0 + 가산」이 이미 그 한 줄에 들어 있다.
    settled: world.settled ?? null,        // { avg, band, mult, loss }

    // ── 재화 ────────────────────────────────────────────────
    // ⚠ **낮 지갑에 더할 값은 `walletDelta` 다.** `cheese` 를 더하면 밤 상점이
    //   공짜가 되고 불변식 ③(밤 소비 = 전투 수입의 50~60%)이 무효가 된다.
    //   ③ 검산을 위해 분자·분모를 함께 받는다.
    cheese: world.cheese,                  // 그 밤 조각의 총 환전액 (③의 분모)
    spent: world.spent,                    // 구매 + 리롤          (③의 분자)
    // ⚠ **이월분을 빼고 돌려준다.** 낮은 이 값을 `cheese += …` 로 **더하므로**,
    //   이월분까지 넣으면 낮 지갑이 매 밤 두 배가 된다(재화 복제).
    //   `walletNet = wallet − walletCarry` = **그 밤의 순증감**이고 음수일 수 있다
    //   (번 것보다 많이 썼다 = 낮에 모아 둔 것을 헐었다 — 그것이 정상이다).
    walletDelta: world.walletNet,          // 그 밤의 순증감 ← 낮 지갑에 더하는 값
    walletCarry: world.walletCarry,        // 검산용 — 무엇을 들고 들어갔는가

    // ── 계측 (낮 화면·일기·검산용) ──────────────────────────
    nightKey: world.nightKey ?? null,
    downAt: m.downAt ?? null,
    bossCleared: boss?.cleared ?? null,
    ...(boss ? { boss } : {}),
    // ⚠ `tracks[i].panic` 은 **종료 시점 플래그**라 회복하면 false 로 돌아간다.
    //   「그 밤에 패닉이 있었는가」는 누적 초로 물어야 한다.
    panicked: (world.tracks?.[world.primary]?.panicSec ?? 0) > 0,
    hpLeft: Math.max(0, Math.round(p.hp ?? 0)),
    maxHp: world.maxHp ?? null,
    kills: (m.killsPlayer ?? 0) + (m.killsDog ?? 0),
    killsDog: m.killsDog ?? 0,
    levelUps: m.levelUpsNight ?? 0,
    picked: Math.round(m.pickedNight ?? 0),
    drinks: m.drinks ?? 0,

    // ── N22 구출 밤 ─────────────────────────────────────────
    // 실패 분기가 없다 — 10:00에 자동으로 찾는다(7-2). 그래도 **언제·어떻게**
    // 찾았는지는 서사가 읽는다(「잃은 양 슬롯」).
    rescue: world.rescue ? {
      foundAt: world.foundAt ?? null,
      foundBy: world.foundBy ?? null,      // '도달' | '자동'
      dist0: world.rescueDist0 ?? null,
    } : null,

    // ── 밤이 **안 내주는 것** (낮이 스스로 알아야 한다) ─────
    // · 개 부상 — 밤의 개 객체에 hp 필드가 없다. 적은 전부 플레이어를 노리므로
    //   개가 맞는 경로 자체가 없다.                        TODO(밤): 개 피격
    // · 어느 양이 죽었는가 — 밤은 개체를 모른다. 수(소수)만 낸다.
    // · 부상(양) — 트리거가 재설계 대기(명세 10-1).
  };
}

// ── 낮 → 밤 ───────────────────────────────────────────────────────
// 밤 World 생성자에 그대로 넣을 opts. **이미 뚫려 있는 자리**는 채우고,
// **아직 없는 자리**는 키만 만들어 null 로 둔다(밤이 무시한다).
export function toNightOpts(state, nightNo) {
  const j = state.journey, f = state.flock;

  return {
    // ── 이미 밤이 읽는 것 ───────────────────────────────────
    nightKey: 'N' + nightNo,
    weapon: state.campaign.weapon,         // config.js 가 === 'pasu' 로 갈린다
    seed: nightSeed(state.campaign.rootSeed, nightNo),
    // ⚠ **무리 배율을 같이 넘기지 마라.** 밤이 flockN 하나에서 스스로 만든다
    //   (`flockMult = 1 + (flockN − 20) / 60`). 둘 다 넘기면 진실이 둘이 된다.
    flockN: flockTotal(f),
    // ⚠ **구출 밤(구 N22)이 삭제됐다** 【사장님 지시 2026-08-03】. 밤에 양이 나오는
    //   유일한 예외였고, 잃은 양은 이제 **마지막 밤 화살표 끝**에서 만난다.
    //   날짜(`nightNo === 22`)를 그대로 뒀으면 40일에서 **비의 밤**이 구출 밤이 됐다.
    rescue: false,
    // 여기까지 **잃은 양이 몇 마리인가** — 마지막 밤 화살표 끝에 그만큼 모여 선다.
    //   한 마리도 안 잃었으면 밤이 스스로 하나를 세운다(`config.FINALE` 주석).
    lostSheep: f.lostInt ?? 0,
    // ⚠ 올무(`snares`)를 보내던 자리다 — **개념째 폐기**【2026-08-07 사장님 지시】.

    // ── 낮 → 밤 【뚫렸다】 ──────────────────────────────────
    // **이 자리들은 전부 `TODO(밤)` 로 비어 있었다.** 그래서 낮 3축을 6단계까지
    // 사도 밤에서 아무 일도 안 일어났다 — 개축 완주에도 개는 2마리였고,
    // 캡스톤을 사도 무료 요령이 안 나왔다. 밤 쪽 문은 `gb/dayin.js` 다.
    //
    // ⚠ **낮 계수와 밤 카드의 합성 규칙은 정본 미기재**이며 `dayin.MERGE` 에
    //   곱연산으로 잠정돼 있다. 확정되면 그 상수 하나만 바꾼다.
    dayMods: resolveDayMods(state),        // 17개 훅 한 덩어리
    stabStart: f.nextStabStart ?? 100,     // 전날 패닉이면 85(명세 5장)
    priorityMode: state.settings.priority, // nearest | elite | far | dense
    // 그 밤 나가는 개 **명단** — 부상 제외·슬롯 제한·손님 개까지 `nightDogs` 가 안다.
    // 서리·바람 중 부상하지 않은 고정 명단만 넘긴다.
    dogs: nightDogs(state),
    // 낮 부품함의 네 장착 칸. 밤의 레벨업은 이 장비를 바꾸지 않고 임시 강화만 얹는다.
    gear: hydrateGear(state.armory, PART_BY_ID),

    // ── 아직 비어 있는 것 ───────────────────────────────────
    // **낮 지갑을 그대로 실어 보낸다**(사장님 지시 2026-07-29 — 「치즈는 모든 날 연속」).
    //   구 값은 `null`(= 이월 없음)이었고, 그래서 **둘째 밤부터 낮에 모아 둔 치즈를
    //   밤 상점에서 못 썼다.** 자리는 있었고 아무도 안 채웠다.
    walletCarry: state.ledger.cheese,
    // 마지막 사자 밤의 개 강제 회복은 **낮이 이미 처리한다**(`forceHealForLionNight`, openDay 안).
    // 남은 것은 N13 서리뿐인데 **밤에 개 개체 지정 훅이 없다.**  TODO(밤): N13 서리
    forcedHeal: null,
  };
}

// ── 시드 ──────────────────────────────────────────────────────────
// **재시도마다 재생성된다**(명세 10장). 그것이 「무비용 결정론적 리허설」을 막는
// 유일한 장치다 — 끝까지 본 뒤 이탈해도 같은 판이 다시 안 온다.
// 순수 함수라 같은 (root, 밤)이면 언제나 같다 — 저장·복원이 시드를 안 든다.
// ⚠ **회차 인자가 있었다** — 재시도마다 다른 시드를 뽑아 「무비용 결정론적 리허설」을
//   막으려던 것이다. 재시도가 사라져(2026-08-03) 같은 밤을 두 번 칠 수 없으므로
//   그 인자도 같이 뺐다. 밤 번호 하나면 시드가 정해진다.
export function nightSeed(rootSeed, nightNo) {
  let h = (rootSeed >>> 0) ^ 0x9e3779b9;
  for (const v of [nightNo]) {
    h ^= (v + 0x6d2b79f5) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 1 | h) >>> 0;
    h = (h + Math.imul(h ^ (h >>> 7), 61 | h)) >>> 0;
  }
  return (h ^ (h >>> 14)) >>> 0;
}

// 무리 총수 = 익명 + 살아 있는 핵심 양. 밤에 넘기는 유일한 무리 수치다.
export function flockTotal(flock) {
  return (flock?.anon ?? 0) + (flock?.core ?? []).filter((s) => s.alive).length;
}
